/**
 * ImageDisplay component - Displays generated images with controls.
 *
 * Design: DENiZEN
 * - Soft shadows and rounded corners
 * - Muted palette with teal accents
 * - Smooth button interactions
 * - WCAG 2.1 AA compliant
 */

import { useState } from 'react'

export interface ImageDisplayProps {
  /**
   * Image URL
   */
  imageUrl: string
  /**
   * Alt text for accessibility
   */
  alt: string
  /**
   * Image title/caption
   */
  title?: string
  /**
   * Iteration number (for multiple images)
   */
  iterationNumber?: number
  /**
   * Whether image is loading
   */
  isLoading?: boolean
  /**
   * Callback when image loads
   */
  onLoad?: () => void
  /**
   * Callback when image fails to load
   */
  onError?: () => void
  /**
   * Show download button
   */
  showDownload?: boolean
  /**
   * Show fullscreen button
   */
  showFullscreen?: boolean
}

export function ImageDisplay({
  imageUrl,
  alt,
  title,
  iterationNumber,
  isLoading = false,
  onLoad,
  onError,
  showDownload = true,
  showFullscreen = true,
}: ImageDisplayProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  const handleImageLoad = () => {
    setImageLoaded(true)
    onLoad?.()
  }

  const handleImageError = () => {
    setImageError(true)
    onError?.()
  }

  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `community-vision${iterationNumber ? `-${iterationNumber}` : ''}.png`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  return (
    <>
      {/* Image container - DENiZEN */}
      <div className="relative group denizen-card overflow-hidden aspect-square">
        {/* Loading state */}
        {(isLoading || !imageLoaded) && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-teal border border-dark-green/15 shadow-denizen rounded-2xl flex items-center justify-center">
                <div className="flex gap-1">
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </div>
              </div>
              <p className="text-sm font-body text-muted">Loading image...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="flex flex-col items-center gap-4 text-center px-4">
              <div className="w-16 h-16 bg-error border border-dark-green/15 shadow-denizen rounded-2xl flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-sm font-display font-bold text-dark-green">Failed to load image</p>
            </div>
          </div>
        )}

        {/* Image */}
        <img
          src={imageUrl}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          className={`
            w-full h-full object-cover
            transition-opacity duration-300
            ${imageLoaded ? 'opacity-100' : 'opacity-0'}
          `}
        />

        {/* Controls overlay - DENiZEN style */}
        {imageLoaded && (showDownload || showFullscreen) && (
          <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {showFullscreen && (
              <button
                type="button"
                onClick={toggleFullscreen}
                className="w-10 h-10 bg-white border border-dark-green/15 shadow-denizen-sm rounded-full flex items-center justify-center hover:-translate-y-1 hover:shadow-denizen transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green"
                aria-label="View fullscreen"
              >
                <svg
                  className="w-5 h-5 text-dark-green"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M15 3.75a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0V5.56l-3.97 3.97a.75.75 0 1 1-1.06-1.06l3.97-3.97h-2.69a.75.75 0 0 1-.75-.75Zm-12 0A.75.75 0 0 1 3.75 3h4.5a.75.75 0 0 1 0 1.5H5.56l3.97 3.97a.75.75 0 0 1-1.06 1.06L4.5 5.56v2.69a.75.75 0 0 1-1.5 0v-4.5Zm11.47 11.78a.75.75 0 1 1 1.06-1.06l3.97 3.97v-2.69a.75.75 0 0 1 1.5 0v4.5a.75.75 0 0 1-.75.75h-4.5a.75.75 0 0 1 0-1.5h2.69l-3.97-3.97Zm-4.94-1.06a.75.75 0 0 1 0 1.06L5.56 19.5h2.69a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 1 1.5 0v2.69l3.97-3.97a.75.75 0 0 1 1.06 0Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
            {showDownload && (
              <button
                type="button"
                onClick={() => void handleDownload()}
                className="w-10 h-10 bg-mid-green border border-dark-green/15 shadow-denizen-sm rounded-full flex items-center justify-center hover:-translate-y-1 hover:shadow-denizen transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green"
                aria-label="Download image"
              >
                <svg
                  className="w-5 h-5 text-dark-green"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2.25a.75.75 0 0 1 .75.75v11.69l3.22-3.22a.75.75 0 1 1 1.06 1.06l-4.5 4.5a.75.75 0 0 1-1.06 0l-4.5-4.5a.75.75 0 1 1 1.06-1.06l3.22 3.22V3a.75.75 0 0 1 .75-.75Zm-9 13.5a.75.75 0 0 1 .75.75v2.25a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5V16.5a.75.75 0 0 1 1.5 0v2.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V16.5a.75.75 0 0 1 .75-.75Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Iteration badge - DENiZEN pebble label */}
        {iterationNumber && imageLoaded && (
          <div className="absolute top-4 left-4">
            <div className="pebble-label">
              V{iterationNumber}
            </div>
          </div>
        )}
      </div>

      {/* Title/Caption - DENiZEN style */}
      {title && imageLoaded && (
        <p className="mt-4 text-center text-sm font-body text-muted border-t border-dark-green/15 pt-3">
          {title}
        </p>
      )}

      {/* Fullscreen modal - DENiZEN overlay */}
      {isFullscreen && (
        <div
          className="fixed inset-0 z-50 bg-dark-green flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Image fullscreen view"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="absolute top-6 right-6 w-14 h-14 bg-white border border-dark-green/15 shadow-denizen rounded-full flex items-center justify-center hover:-translate-y-1 hover:shadow-denizen transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green z-10"
            aria-label="Close fullscreen"
          >
            <svg
              className="w-7 h-7 text-dark-green"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Fullscreen image */}
          <img
            src={imageUrl}
            alt={alt}
            className="max-w-full max-h-full object-contain p-8"
          />

          {/* Download button in fullscreen */}
          {showDownload && (
            <button
              type="button"
              onClick={() => void handleDownload()}
              className="absolute bottom-6 right-6 btn-denizen-primary"
              aria-label="Download image"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2.25a.75.75 0 0 1 .75.75v11.69l3.22-3.22a.75.75 0 1 1 1.06 1.06l-4.5 4.5a.75.75 0 0 1-1.06 0l-4.5-4.5a.75.75 0 1 1 1.06-1.06l3.22 3.22V3a.75.75 0 0 1 .75-.75Zm-9 13.5a.75.75 0 0 1 .75.75v2.25a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5V16.5a.75.75 0 0 1 1.5 0v2.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V16.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
              </svg>
              Download
            </button>
          )}
        </div>
      )}
    </>
  )
}

export default ImageDisplay
